import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { CardTitle, EmptyState, FieldGroup, Text } from "@/components/ui/text";
import { ErrorBanner } from "@/components/ui/error-banner";
import { FormGrid } from "@/components/ui/form-grid";
import { Icon } from "@/components/ui/icons";
import { IconButton } from "@/components/ui/icon-button";
import { LanguageSelector } from "@/components/ui/language-selector";
import { LoadingContainer } from "@/components/ui/spinner";
import { HStack } from "@/components/ui/h-stack";
import { Surface } from "@/components/ui/surface";
import { PageTitle } from "@/contexts/page-title-context";
import { Heading } from "@/components/ui/heading";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { VStack } from "@/components/ui/v-stack";
import { IconCircle } from "@/components/ui/icon-circle";
import { apiClient } from "@/lib/api/client";
import type { PaginatedResponse } from "@/lib/api/types";
import { signOut } from "@/lib/api/auth";
import type { ApplicationStatus } from "@/lib/api/enums";
import type { EntityApplication } from "@/lib/api/applications";
import { fromIntlLanguage, getSystemLanguage } from "@/lib/i18n/config";
import {
  getApplicationStatusKey,
  getApplicationStatusColor,
} from "@/lib/status-config";

function ApplicationStatusPage() {
  const language = fromIntlLanguage(useTranslation().i18n.language);
  const { t: tApplication } = useTranslation("application");
  const { t: tApplicationStatusPage } = useTranslation(
    "application.statusPage",
  );
  const { t: tApplicationStatus } = useTranslation(
    "application.statusPage.status",
  );
  const { t: tApplicationStatusDescription } = useTranslation(
    "application.statusPage.description",
  );
  const { t: tApplicationStatusFields } = useTranslation(
    "application.statusPage.fields",
  );
  const { t: tApplicationStatusActions } = useTranslation(
    "application.statusPage.actions",
  );
  const { t: tAuth } = useTranslation("auth");
  const { t: tCommon } = useTranslation("common");
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [errorKind, setErrorKind] = useState<"load" | null>(null);
  const [applications, setApplications] = useState<EntityApplication[]>([]);

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    setErrorKind(null);

    try {
      const result = await apiClient.get<PaginatedResponse<EntityApplication>>(
        "/entity-applications",
      );
      setApplications(
        result.data.toSorted(
          (a, b) =>
            new Date(b.modified_at).getTime() -
            new Date(a.modified_at).getTime(),
        ),
      );
    } catch (err) {
      console.error("Failed to fetch applications:", err);
      setErrorKind("load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  // Get the most recent application in review (not approved yet)
  const activeApplication = applications.find(
    (app) => app.is_approved === false,
  );
  const latestApplication = applications[0]; // Assuming sorted by date desc

  const displayApplication = activeApplication || latestApplication;

  // Derive status from is_approved field
  const getApplicationStatus = (app: EntityApplication): ApplicationStatus => {
    if (app.is_approved) {
      return "Approved";
    }
    // If not approved, check if it's been reviewed (has status field) or still in review
    return app.status || "InReview";
  };

  const displayStatus = displayApplication
    ? getApplicationStatus(displayApplication)
    : "InReview";

  const formatEntityType = (entityType: string): string => {
    if (entityType === "Shipper") return tApplication("shipper");
    if (entityType === "TransportCompany") return tApplication("carrier");
    if (entityType === "Broker") return tApplication("broker");
    if (entityType === "Driver") return tApplication("driver");
    return entityType;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString(getSystemLanguage(language), {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <VStack className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-[color:var(--background)] dark:to-[color:var(--surface-container)]">
      {/* Header */}
      <header className="bg-[color:var(--surface-container)] border-b border-[color:var(--outline-variant)] px-6 py-4">
        <HStack justify="between" className="max-w-4xl mx-auto">
          <HStack>
            <Surface
              bg="primary"
              rounded="lg"
              className="flex w-8 h-8 items-center justify-center"
            >
              <Text color="on-primary" weight="bold" size="lg">
                Y
              </Text>
            </Surface>
            <Heading size="lg" className="!font-bold">
              Yuklar
            </Heading>
          </HStack>
          <HStack>
            <ThemeToggle />
            <LanguageSelector compact />
            <IconButton
              type="button"
              onClick={async () => {
                await signOut();
                void navigate({ replace: true, to: "/login" });
              }}
              className="text-[color:var(--on-surface-variant)] hover:text-[color:var(--on-surface)]"
              title={tAuth("signOut")}
            >
              <Icon.LogOut size="md" />
            </IconButton>
          </HStack>
        </HStack>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-12">
        <PageTitle>{tApplicationStatusPage("title")}</PageTitle>
        {loading ? (
          <LoadingContainer size="lg" className="h-96">
            <Icon.Spinner size="xl" color="primary" />
          </LoadingContainer>
        ) : errorKind ? (
          <ErrorBanner
            title={tCommon("errorOccurred")}
            description={tCommon("errorLoadingData")}
            onRetry={fetchApplications}
            retryLabel={tApplicationStatusPage("retry")}
          />
        ) : applications.length === 0 ? (
          <Card variant="bordered" padding="none">
            <EmptyState
              icon={<Icon.Document size="xl" color="muted" />}
              title={tApplicationStatusPage("noApplicationsTitle")}
              description={tApplicationStatusPage("noApplicationsDescription")}
              action={
                <Button
                  type="button"
                  onClick={() => void navigate({ to: "/apply" })}
                >
                  {tApplicationStatusPage("applyNow")}
                </Button>
              }
            />
          </Card>
        ) : displayApplication ? (
          <VStack gap="xl">
            {(() => {
              const statusKey = getApplicationStatusKey(displayApplication);
              const statusColor = getApplicationStatusColor(displayApplication);
              const statusIcon =
                statusKey === "approved" ? (
                  <Icon.CheckCircle size="xl" color="success" />
                ) : statusKey === "rejected" ? (
                  <Icon.XCircle size="xl" color="danger" />
                ) : statusKey === "rejectedTemporary" ? (
                  <Icon.Alert size="xl" color="warning" />
                ) : (
                  <Icon.ClockOutline size="xl" color="warning" />
                );

              return (
                <Card variant="bordered" padding="none">
                  <CardHeader>
                    <CardTitle>{tApplicationStatusPage("title")}</CardTitle>
                  </CardHeader>
                  <CardContent className="text-center space-y-3">
                    <IconCircle size="2xl" color="muted" className="mx-auto">
                      {statusIcon}
                    </IconCircle>
                    <HStack justify="center">
                      <Badge
                        color={statusColor}
                        data-testid="application-status-badge"
                      >
                        {tApplicationStatus(statusKey)}
                      </Badge>
                    </HStack>
                    <HStack justify="center">
                      <Text color="muted" className="max-w-md">
                        {tApplicationStatusDescription(statusKey)}
                      </Text>
                    </HStack>
                  </CardContent>
                </Card>
              );
            })()}

            {/* Application Details */}
            <Card variant="bordered" padding="none">
              <CardHeader>
                <CardTitle>{tApplicationStatusPage("detailsTitle")}</CardTitle>
              </CardHeader>
              <CardContent>
                <FormGrid cols="sm" gap="xl">
                  <FieldGroup
                    label={tApplicationStatusFields("applicationNumber")}
                    value={`#${displayApplication.apply_form_number}`}
                    valueClassName="text-sm"
                  />
                  <FieldGroup
                    label={tApplicationStatusFields("entityType")}
                    value={formatEntityType(displayApplication.entity_type)}
                    valueClassName="text-sm"
                  />
                  <FieldGroup
                    label={tApplicationStatusFields("name")}
                    value={displayApplication.name}
                    valueClassName="text-sm"
                  />
                  <FieldGroup
                    label={tApplicationStatusFields("submitted")}
                    value={formatDate(displayApplication.created_at)}
                    valueClassName="text-sm"
                  />
                  <FieldGroup
                    label={tApplicationStatusFields("location")}
                    value={`${displayApplication.city?.name ?? "—"}, ${displayApplication.country?.name ?? "—"}`}
                    valueClassName="text-sm"
                  />
                </FormGrid>
              </CardContent>
            </Card>

            {/* Actions */}
            <HStack justify="center" gap="xl">
              {displayStatus === "Approved" && (
                <Button
                  type="button"
                  onClick={() => void navigate({ to: "/dashboard" })}
                  size="lg"
                >
                  {tApplicationStatusActions("goToDashboard")}
                </Button>
              )}

              {displayStatus === "RejectedTemporarily" && (
                <Button
                  type="button"
                  onClick={() => void navigate({ to: "/apply" })}
                  size="lg"
                >
                  {tApplicationStatusActions("resubmit")}
                </Button>
              )}

              {displayStatus === "Rejected" && (
                <Button
                  type="button"
                  onClick={() => void navigate({ to: "/apply" })}
                  color="neutral"
                  size="lg"
                >
                  {tApplicationStatusActions("newApplication")}
                </Button>
              )}

              <Button
                type="button"
                onClick={fetchApplications}
                color="neutral"
                variant="outline"
                size="lg"
              >
                <Icon.ArrowPath size="md" />
                {tApplicationStatusActions("refresh")}
              </Button>
            </HStack>
          </VStack>
        ) : null}
      </main>
    </VStack>
  );
}

export const Route = createFileRoute("/_app/application-status")({
  component: ApplicationStatusPage,
});
